import React, { useState } from 'react';
import { Loader } from '@mantine/core';
import { toast } from 'react-toastify';
import { checkChallengeApplicationEligibility, createChallengeApplication } from '../../../services/inbound.service';
import AlreadyAppliedModal from '../AlreadyAppliedModal/AlreadyAppliedModal';
import {
  ApplicationErrors,
  ApplicationFieldName,
  initialPersonalDetails,
  initialShippingAddress,
  normalizeInstagramUsername,
  validateApplication,
} from '../helpers/validateApplication';
import { PersonalDetails, ShippingAddress } from './FormSections';
import styles from './ApplicationForm.module.css';

type ApplicationFormProps = {
  challengeId: string;
  showEmailTooltip: boolean;
  className?: string;
};

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export default function ApplicationForm({ challengeId, showEmailTooltip, className = '' }: ApplicationFormProps) {
  const [details, setDetails] = useState(initialPersonalDetails);
  const [address, setAddress] = useState(initialShippingAddress);
  const [errors, setErrors] = useState<ApplicationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAlreadyApplied, setShowAlreadyApplied] = useState(false);

  const clearErrors = (...fields: ApplicationFieldName[]) => {
    setErrors((prev) => {
      if (!fields.some((field) => prev[field])) return prev;
      const next = { ...prev };
      fields.forEach((field) => delete next[field]);
      return next;
    });
  };

  const handleDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDetails((prev) => ({ ...prev, [name]: value }));
    clearErrors(name as ApplicationFieldName);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    const validationErrors = validateApplication(details, address);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;

    const email = details.email.trim();
    const instagramUsername = normalizeInstagramUsername(details.instagramUsername);
    const addressLineTwo = address.addressLineTwo.trim();

    setIsSubmitting(true);
    try {
      const eligibility = await checkChallengeApplicationEligibility(challengeId, { email, instagramUsername });
      if (eligibility.status !== true) {
        toast.error(eligibility.message || DEFAULT_ERROR_MESSAGE);
        return;
      }
      if (eligibility.data.status === 'duplicate_application') {
        setShowAlreadyApplied(true);
        return;
      }
      if (eligibility.data.status === 'already_member') {
        toast.error("You're already a part of this challenge.");
        return;
      }

      const response = await createChallengeApplication(challengeId, {
        name: details.name.trim(),
        email,
        instagramUsername,
        shippingAddress: {
          addressLineOne: address.addressLineOne.trim(),
          ...(addressLineTwo && { addressLineTwo }),
          pincode: address.pincode.trim(),
          city: address.city.trim(),
          state: address.state.trim(),
          country: address.country.trim(),
        },
      });
      if (response.status !== true) {
        toast.error(response.message || DEFAULT_ERROR_MESSAGE);
        return;
      }

      toast.success('Application sent!');
      setDetails(initialPersonalDetails);
      setAddress(initialShippingAddress);
    } catch (err) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form className={`${styles.form} ${className}`} onSubmit={handleSubmit} noValidate>
        <PersonalDetails
          details={details}
          errors={errors}
          onChange={handleDetailsChange}
          showEmailTooltip={showEmailTooltip}
        />
        <ShippingAddress address={address} setAddress={setAddress} errors={errors} clearErrors={clearErrors} />
        <button
          type="submit"
          className={`${styles.applyButton} ${isSubmitting ? styles.applyButtonLoading : ''}`}
          disabled={isSubmitting}
        >
          {isSubmitting && <Loader color="white" size={16} />}
          {isSubmitting ? 'Applying' : 'Apply'}
        </button>
      </form>
      <AlreadyAppliedModal open={showAlreadyApplied} onClose={() => setShowAlreadyApplied(false)} />
    </>
  );
}
